import { z } from "../wire/zod";

export const createDependenciesResponseSchema = z.object({
  dependencies: z.array(
    z.object({
      id: z.string(),
      sessionId: z.string(),
      dependentSessionId: z.string(),
      dependencyType: z.enum(["blocks", "requires", "related"]),
      createdAt: z.number().int().optional(),
    }),
  ),
});
export const createLinkTypeResponseSchema = z.object({
  linkType: z.object({
    id: z.string(),
    sessionId: z.string(),
    fromStickyType: z.string(),
    toStickyType: z.string(),
  }),
});
export const createLinksResponseSchema = z.object({
  links: z.array(
    z.object({
      id: z.string(),
      sessionId: z.string(),
      fromStickyId: z.string(),
      toStickyId: z.string(),
      linkTypeId: z.string().optional(),
    }),
  ),
});
export const createTimerEventResponseSchema = z.object({ eventId: z.string().optional() });
export const getAlmStickiesAcrossSessionsResponseSchema = z.object({
  stickies: z.array(
    z
      .object({
        sticky: z.object({
          id: z.string(),
          almIssueId: z.union([z.number().int(), z.string()]).optional(),
          boardId: z.string(),
          WSJFValue: z.union([z.number(), z.null()]).optional(),
          almIssueUrl: z.string().optional(),
          stackPos: z.number().int().optional(),
          text: z.string().optional(),
          typeId: z.string(),
          statusClass: z.enum(["todo", "in-progress", "done", "undefined"]).optional(),
          status: z.string().optional(),
          aCenter: z.tuple([z.number(), z.number()]).optional(),
          teamId: z.union([z.number().int(), z.null()]).optional(),
          iterationNumber: z.union([z.number().int(), z.null()]).optional(),
          almSource: z.union([z.number().int(), z.string(), z.null()]).optional(),
          storyPoints: z.union([z.number(), z.null()]).optional(),
          risk: z.enum(["R", "O", "A", "M", ""]).optional(),
          dependTeamId: z.union([z.number().int(), z.null()]).optional(),
          dependTeam: z.union([z.string(), z.null()]).optional(),
          objectives: z.array(z.object({ id: z.string(), boardId: z.string() })).optional(),
          precondTeamId: z.number().int().optional(),
          precondTeam: z.string().optional(),
          flagType: z.union([z.string(), z.null()]).optional(),
          stickyGroup: z.union([z.string(), z.null()]).optional(),
          artId: z.union([z.number().int(), z.null()]).optional(),
          transition: z.string().optional(),
          reactions: z.record(z.string(), z.array(z.string())).optional(),
          assignee: z
            .union([
              z.object({
                pipId: z
                  .union([
                    z.string().describe("PI planning user id, null when the user is not mapped"),
                    z.null().describe("PI planning user id, null when the user is not mapped"),
                  ])
                  .describe("PI planning user id, null when the user is not mapped"),
                almId: z
                  .union([
                    z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                    z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
                  ])
                  .describe("ALM user id, null when the user is not a mapped ALM user"),
              }),
              z.null(),
            ])
            .optional(),
          reporter: z
            .union([
              z.object({
                pipId: z
                  .union([
                    z.string().describe("PI planning user id, null when the user is not mapped"),
                    z.null().describe("PI planning user id, null when the user is not mapped"),
                  ])
                  .describe("PI planning user id, null when the user is not mapped"),
                almId: z
                  .union([
                    z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                    z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
                  ])
                  .describe("ALM user id, null when the user is not a mapped ALM user"),
              }),
              z.null(),
            ])
            .optional(),
          stepValue: z
            .union([
              z
                .number()
                .int()
                .describe(
                  "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
                ),
              z
                .null()
                .describe(
                  "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
                ),
            ])
            .describe(
              "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
            )
            .optional(),
          templateId: z.string().optional(),
          isOrigin: z.boolean().optional(),
          linkedStickies: z
            .array(
              z.object({
                linkId: z.string(),
                stickyId: z.string(),
                direction: z.enum(["from", "to"]),
              }),
            )
            .optional(),
          statusDistribution: z
            .union([
              z
                .record(z.string(), z.object({ points: z.number(), value: z.number().int() }))
                .describe("Progress of the linked child stickies by status class"),
              z.null(),
            ])
            .optional(),
        }),
        sessionId: z.string(),
        boardId: z.string(),
        boardType: z.enum([
          "team",
          "risk",
          "program",
          "solution",
          "solution_backlog",
          "backlog",
          "flex",
        ]),
        stickyType: z.object({
          id: z.string(),
          sessionId: z.string(),
          name: z.string(),
          color: z.tuple([z.number(), z.number(), z.number(), z.number()]),
          altColor: z
            .union([z.tuple([z.number(), z.number(), z.number(), z.number()]), z.null()])
            .optional(),
          originBoardType: z.enum([
            "team",
            "risk",
            "program",
            "solution",
            "solution_backlog",
            "backlog",
            "flex",
          ]),
          originFlexboardType: z.string().optional(),
          almType: z.union([z.string(), z.null()]).optional(),
          availableBoardTypes: z.array(
            z.enum(["team", "risk", "program", "solution", "solution_backlog", "backlog", "flex"]),
          ),
          availableFlexboardTypes: z.array(z.string()).optional(),
          templateId: z.string().optional(),
          functionality: z.enum(["workitem", "note", "risk", "dependency"]),
          stepEstimate: z.boolean().optional(),
          stepEstimateValues: z
            .array(z.object({ text: z.string(), value: z.number().int() }))
            .optional(),
          index: z.number().int().describe("Sort order in the session's palette").optional(),
        }),
      })
      .describe("Sticky with session context for cross-session queries"),
  ),
});
export const getAlmStickiesResponseSchema = z.object({
  stickies: z.array(
    z.object({
      id: z.string(),
      almIssueId: z.union([z.number().int(), z.string()]).optional(),
      boardId: z.string(),
      WSJFValue: z.union([z.number(), z.null()]).optional(),
      almIssueUrl: z.string().optional(),
      stackPos: z.number().int().optional(),
      text: z.string().optional(),
      typeId: z.string(),
      statusClass: z.enum(["todo", "in-progress", "done", "undefined"]).optional(),
      status: z.string().optional(),
      aCenter: z.tuple([z.number(), z.number()]).optional(),
      teamId: z.union([z.number().int(), z.null()]).optional(),
      iterationNumber: z.union([z.number().int(), z.null()]).optional(),
      almSource: z.union([z.number().int(), z.string(), z.null()]).optional(),
      storyPoints: z.union([z.number(), z.null()]).optional(),
      risk: z.enum(["R", "O", "A", "M", ""]).optional(),
      dependTeamId: z.union([z.number().int(), z.null()]).optional(),
      dependTeam: z.union([z.string(), z.null()]).optional(),
      objectives: z.array(z.object({ id: z.string(), boardId: z.string() })).optional(),
      precondTeamId: z.number().int().optional(),
      precondTeam: z.string().optional(),
      flagType: z.union([z.string(), z.null()]).optional(),
      stickyGroup: z.union([z.string(), z.null()]).optional(),
      artId: z.union([z.number().int(), z.null()]).optional(),
      transition: z.string().optional(),
      reactions: z.record(z.string(), z.array(z.string())).optional(),
      assignee: z
        .union([
          z.object({
            pipId: z
              .union([
                z.string().describe("PI planning user id, null when the user is not mapped"),
                z.null().describe("PI planning user id, null when the user is not mapped"),
              ])
              .describe("PI planning user id, null when the user is not mapped"),
            almId: z
              .union([
                z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
              ])
              .describe("ALM user id, null when the user is not a mapped ALM user"),
          }),
          z.null(),
        ])
        .optional(),
      reporter: z
        .union([
          z.object({
            pipId: z
              .union([
                z.string().describe("PI planning user id, null when the user is not mapped"),
                z.null().describe("PI planning user id, null when the user is not mapped"),
              ])
              .describe("PI planning user id, null when the user is not mapped"),
            almId: z
              .union([
                z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
              ])
              .describe("ALM user id, null when the user is not a mapped ALM user"),
          }),
          z.null(),
        ])
        .optional(),
      stepValue: z
        .union([
          z
            .number()
            .int()
            .describe(
              "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
            ),
          z
            .null()
            .describe(
              "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
            ),
        ])
        .describe(
          "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
        )
        .optional(),
      templateId: z.string().optional(),
      isOrigin: z.boolean().optional(),
      linkedStickies: z
        .array(
          z.object({ linkId: z.string(), stickyId: z.string(), direction: z.enum(["from", "to"]) }),
        )
        .optional(),
      statusDistribution: z
        .union([
          z
            .record(z.string(), z.object({ points: z.number(), value: z.number().int() }))
            .describe("Progress of the linked child stickies by status class"),
          z.null(),
        ])
        .optional(),
    }),
  ),
});
export const getArtsResponseSchema = z.object({
  arts: z.array(
    z.object({
      id: z.number().int(),
      name: z.string(),
      teams: z.array(
        z.object({ id: z.number().int(), name: z.string(), external: z.boolean().optional() }),
      ),
    }),
  ),
});
export const getBoardIterationsResponseSchema = z.object({
  iterationsByBoard: z
    .record(
      z.string(),
      z.array(
        z.object({
          boardIterationId: z.number().int(),
          velocity: z.union([z.number(), z.string()]),
          statusDistribution: z
            .record(z.string(), z.object({ points: z.number(), value: z.number().int() }))
            .describe("Progress of the linked child stickies by status class")
            .optional(),
        }),
      ),
    )
    .describe("Keyed by team board id"),
});
export const getBoardResponseSchema = z.object({
  board: z.union([
    z.object({
      id: z.string(),
      sessionId: z.string().optional(),
      boardType: z.literal("team"),
      stickyScale: z.number().optional(),
      teamId: z.number().int(),
      teamName: z.union([z.string(), z.null()]).optional(),
      artId: z.number().int().optional(),
      shapes: z
        .array(
          z.object({
            id: z.string(),
            type: z.literal("line"),
            fixed: z.boolean(),
            p0: z.object({ x: z.number(), y: z.number() }),
            p1: z.object({ x: z.number(), y: z.number() }),
            label: z
              .union([
                z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                z.null(),
              ])
              .optional(),
          }),
        )
        .optional(),
    }),
    z.object({
      id: z.string(),
      sessionId: z.string().optional(),
      boardType: z.literal("risk"),
      stickyScale: z.number().optional(),
      artId: z.number().int().optional(),
      shapes: z
        .array(
          z.object({
            id: z.string(),
            type: z.literal("line"),
            fixed: z.boolean(),
            p0: z.object({ x: z.number(), y: z.number() }),
            p1: z.object({ x: z.number(), y: z.number() }),
            label: z
              .union([
                z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                z.null(),
              ])
              .optional(),
          }),
        )
        .optional(),
    }),
    z.object({
      id: z.string(),
      sessionId: z.string().optional(),
      boardType: z.literal("program"),
      stickyScale: z.number().optional(),
      artId: z.number().int().optional(),
      swimlanes: z
        .array(
          z.object({
            type: z.enum(["milestones", "team", "external", "other_arts", "empty"]),
            name: z.string().optional(),
            teamIds: z.array(z.number().int()).optional(),
            removableTeamIds: z.array(z.number().int()).optional(),
          }),
        )
        .optional(),
      shapes: z
        .array(
          z.object({
            id: z.string(),
            type: z.literal("line"),
            fixed: z.boolean(),
            p0: z.object({ x: z.number(), y: z.number() }),
            p1: z.object({ x: z.number(), y: z.number() }),
            label: z
              .union([
                z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                z.null(),
              ])
              .optional(),
          }),
        )
        .optional(),
    }),
    z.object({
      id: z.string(),
      sessionId: z.string().optional(),
      boardType: z.literal("backlog"),
      stickyScale: z.number().optional(),
      artId: z.number().int().optional(),
      shapes: z
        .array(
          z.object({
            id: z.string(),
            type: z.literal("line"),
            fixed: z.boolean(),
            p0: z.object({ x: z.number(), y: z.number() }),
            p1: z.object({ x: z.number(), y: z.number() }),
            label: z
              .union([
                z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                z.null(),
              ])
              .optional(),
          }),
        )
        .optional(),
    }),
    z.object({
      id: z.string(),
      sessionId: z.string().optional(),
      boardType: z.literal("solution"),
      shapes: z
        .array(
          z.object({
            id: z.string(),
            type: z.literal("line"),
            fixed: z.boolean(),
            p0: z.object({ x: z.number(), y: z.number() }),
            p1: z.object({ x: z.number(), y: z.number() }),
            label: z
              .union([
                z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                z.null(),
              ])
              .optional(),
          }),
        )
        .optional(),
    }),
    z.object({
      id: z.string(),
      sessionId: z.string().optional(),
      boardType: z.literal("solution_backlog"),
      shapes: z
        .array(
          z.object({
            id: z.string(),
            type: z.literal("line"),
            fixed: z.boolean(),
            p0: z.object({ x: z.number(), y: z.number() }),
            p1: z.object({ x: z.number(), y: z.number() }),
            label: z
              .union([
                z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                z.null(),
              ])
              .optional(),
          }),
        )
        .optional(),
    }),
    z.object({
      id: z.string(),
      sessionId: z.string().optional(),
      boardType: z.literal("flex"),
      flexboardType: z.string(),
      name: z.string(),
      shapes: z
        .array(
          z.object({
            id: z.string(),
            type: z.literal("line"),
            fixed: z.boolean(),
            p0: z.object({ x: z.number(), y: z.number() }),
            p1: z.object({ x: z.number(), y: z.number() }),
            label: z
              .union([
                z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                z.null(),
              ])
              .optional(),
          }),
        )
        .optional(),
    }),
  ]),
});
export const getBoardsResponseSchema = z.object({
  boards: z.array(
    z.union([
      z.object({
        id: z.string(),
        sessionId: z.string().optional(),
        boardType: z.literal("team"),
        stickyScale: z.number().optional(),
        teamId: z.number().int(),
        teamName: z.union([z.string(), z.null()]).optional(),
        artId: z.number().int().optional(),
        shapes: z
          .array(
            z.object({
              id: z.string(),
              type: z.literal("line"),
              fixed: z.boolean(),
              p0: z.object({ x: z.number(), y: z.number() }),
              p1: z.object({ x: z.number(), y: z.number() }),
              label: z
                .union([
                  z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                  z.null(),
                ])
                .optional(),
            }),
          )
          .optional(),
      }),
      z.object({
        id: z.string(),
        sessionId: z.string().optional(),
        boardType: z.literal("risk"),
        stickyScale: z.number().optional(),
        artId: z.number().int().optional(),
        shapes: z
          .array(
            z.object({
              id: z.string(),
              type: z.literal("line"),
              fixed: z.boolean(),
              p0: z.object({ x: z.number(), y: z.number() }),
              p1: z.object({ x: z.number(), y: z.number() }),
              label: z
                .union([
                  z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                  z.null(),
                ])
                .optional(),
            }),
          )
          .optional(),
      }),
      z.object({
        id: z.string(),
        sessionId: z.string().optional(),
        boardType: z.literal("program"),
        stickyScale: z.number().optional(),
        artId: z.number().int().optional(),
        swimlanes: z
          .array(
            z.object({
              type: z.enum(["milestones", "team", "external", "other_arts", "empty"]),
              name: z.string().optional(),
              teamIds: z.array(z.number().int()).optional(),
              removableTeamIds: z.array(z.number().int()).optional(),
            }),
          )
          .optional(),
        shapes: z
          .array(
            z.object({
              id: z.string(),
              type: z.literal("line"),
              fixed: z.boolean(),
              p0: z.object({ x: z.number(), y: z.number() }),
              p1: z.object({ x: z.number(), y: z.number() }),
              label: z
                .union([
                  z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                  z.null(),
                ])
                .optional(),
            }),
          )
          .optional(),
      }),
      z.object({
        id: z.string(),
        sessionId: z.string().optional(),
        boardType: z.literal("backlog"),
        stickyScale: z.number().optional(),
        artId: z.number().int().optional(),
        shapes: z
          .array(
            z.object({
              id: z.string(),
              type: z.literal("line"),
              fixed: z.boolean(),
              p0: z.object({ x: z.number(), y: z.number() }),
              p1: z.object({ x: z.number(), y: z.number() }),
              label: z
                .union([
                  z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                  z.null(),
                ])
                .optional(),
            }),
          )
          .optional(),
      }),
      z.object({
        id: z.string(),
        sessionId: z.string().optional(),
        boardType: z.literal("solution"),
        shapes: z
          .array(
            z.object({
              id: z.string(),
              type: z.literal("line"),
              fixed: z.boolean(),
              p0: z.object({ x: z.number(), y: z.number() }),
              p1: z.object({ x: z.number(), y: z.number() }),
              label: z
                .union([
                  z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                  z.null(),
                ])
                .optional(),
            }),
          )
          .optional(),
      }),
      z.object({
        id: z.string(),
        sessionId: z.string().optional(),
        boardType: z.literal("solution_backlog"),
        shapes: z
          .array(
            z.object({
              id: z.string(),
              type: z.literal("line"),
              fixed: z.boolean(),
              p0: z.object({ x: z.number(), y: z.number() }),
              p1: z.object({ x: z.number(), y: z.number() }),
              label: z
                .union([
                  z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                  z.null(),
                ])
                .optional(),
            }),
          )
          .optional(),
      }),
      z.object({
        id: z.string(),
        sessionId: z.string().optional(),
        boardType: z.literal("flex"),
        flexboardType: z.string(),
        name: z.string(),
        shapes: z
          .array(
            z.object({
              id: z.string(),
              type: z.literal("line"),
              fixed: z.boolean(),
              p0: z.object({ x: z.number(), y: z.number() }),
              p1: z.object({ x: z.number(), y: z.number() }),
              label: z
                .union([
                  z.object({ text: z.string(), align: z.enum(["start", "center", "end"]) }),
                  z.null(),
                ])
                .optional(),
            }),
          )
          .optional(),
      }),
    ]),
  ),
});
export const getBurndownChartDataResponseSchema = z.object({
  dataPoints: z
    .array(
      z
        .object({
          date: z.string().date().describe("Date in YYYY-MM-DD format"),
          openPoints: z
            .union([
              z.number().describe("Total open story points at end of day, null for future dates"),
              z.null().describe("Total open story points at end of day, null for future dates"),
            ])
            .describe("Total open story points at end of day, null for future dates")
            .optional(),
          openCount: z
            .union([
              z.number().int().describe("Total open stickies at end of day, null for future dates"),
              z.null().describe("Total open stickies at end of day, null for future dates"),
            ])
            .describe("Total open stickies at end of day, null for future dates")
            .optional(),
          changes: z
            .union([
              z
                .object({
                  completedPoints: z.number().describe("Story points moved to done status"),
                  completedCount: z
                    .number()
                    .int()
                    .describe("Number of stickies moved to done status"),
                  addedPoints: z
                    .number()
                    .describe("Story points added (new stickies or moved into scope)"),
                  addedCount: z.number().int().describe("Number of stickies added"),
                  removedPoints: z
                    .number()
                    .describe("Story points removed (deleted or moved out of scope)"),
                  removedCount: z.number().int().describe("Number of stickies removed"),
                  reestimatedPoints: z
                    .number()
                    .describe("Net change in story points from re-estimation"),
                })
                .describe("Summary of changes for a single day in a burndown chart"),
              z.null(),
            ])
            .describe("Summary of changes that occurred this day, null for future dates")
            .optional(),
        })
        .describe("A single day's data point in a burndown chart"),
    )
    .describe("Daily burndown data points"),
});
export const getCapacityAllocationResponseSchema = z.object({
  items: z
    .array(
      z
        .object({
          teamId: z.string(),
          typeId: z.string(),
          iterationId: z.union([z.number().int(), z.null()]).optional(),
          items: z.number().int(),
          points: z.number(),
        })
        .describe("Story points for a team/type/iteration combination"),
    )
    .describe("Flat array of capacity allocation items"),
  warnings: z.array(z.string()).describe("Warnings about missing or invalid data").default([]),
});
export const getCfdChartDataResponseSchema = z.object({
  days: z
    .array(
      z
        .object({
          date: z.string().date().describe("Date in YYYY-MM-DD format"),
          bands: z
            .union([
              z.array(
                z
                  .object({
                    status: z
                      .string()
                      .describe(
                        "Raw ALM status name (falls back to the status_class label when unmapped)",
                      ),
                    statusClass: z.enum(["todo", "in-progress", "done", "undefined"]),
                    count: z.number().int().describe("Number of stickies in this band"),
                    points: z.number().describe("Sum of story points in this band"),
                  })
                  .describe("Count and story points of stickies in one status band on a given day"),
              ),
              z.null(),
            ])
            .describe("Per-status bands for the day, null for future dates")
            .optional(),
        })
        .describe(
          "A single day in a cumulative flow diagram: its status bands (or null for future days)",
        ),
    )
    .describe("Per-day cumulative flow data"),
});
export const getDependenciesResponseSchema = z.object({
  dependencies: z.array(
    z.object({
      id: z.string(),
      sessionId: z.string(),
      dependentSessionId: z.string(),
      dependencyType: z.enum(["blocks", "requires", "related"]),
      createdAt: z.number().int().optional(),
    }),
  ),
});
export const getDependencyFlowResponseSchema = z.object({
  items: z
    .array(
      z
        .object({
          precondTeam: z.string().describe("Team that is blocked"),
          precondTeamId: z.number().int().describe("ID of team that is blocked"),
          dependTeam: z
            .union([
              z.string().describe("Team that is blocking, null for unassigned"),
              z.null().describe("Team that is blocking, null for unassigned"),
            ])
            .describe("Team that is blocking, null for unassigned")
            .optional(),
          dependTeamId: z
            .union([
              z.number().int().describe("ID of team that is blocking, null for unassigned"),
              z.null().describe("ID of team that is blocking, null for unassigned"),
            ])
            .describe("ID of team that is blocking, null for unassigned")
            .optional(),
          count: z.number().int().describe("Number of dependency stickies between these teams"),
        })
        .describe("Represents dependency flow between teams for Sankey visualization"),
    )
    .describe("Array of dependency flow items for Sankey visualization"),
  warnings: z.array(z.string()).describe("Warnings about missing or invalid data").default([]),
});
export const getGroupBoardIdsResponseSchema = z
  .object({
    boardIds: z.array(z.string()),
    stickies: z.array(z.object({ boardId: z.string(), stickyId: z.string() })),
  })
  .describe("Every board the sticky is present on (origin and mirrors) with its id there");
export const getIterationsResponseSchema = z.object({
  iterations: z.array(
    z.object({
      id: z.number().int(),
      start: z.number().int(),
      end: z.number().int(),
      name: z.string(),
      teams: z
        .array(
          z.object({
            teamId: z.number().int(),
            sources: z.array(
              z.object({
                id: z.number().int().optional(),
                sprint: z.union([z.number().int(), z.null()]).optional(),
              }),
            ),
          }),
        )
        .optional(),
    }),
  ),
});
export const getLinkTypeResponseSchema = z.object({
  linkType: z
    .object({
      id: z.string(),
      sessionId: z.string(),
      fromStickyType: z.string(),
      toStickyType: z.string(),
    })
    .optional(),
});
export const getLinkTypesResponseSchema = z.object({
  linkTypes: z.array(
    z.object({
      id: z.string(),
      sessionId: z.string(),
      fromStickyType: z.string(),
      toStickyType: z.string(),
    }),
  ),
});
export const getLinksResponseSchema = z.object({
  links: z.array(
    z.object({
      id: z.string(),
      sessionId: z.string(),
      fromStickyId: z.string(),
      toStickyId: z.string(),
      linkTypeId: z.string().optional(),
    }),
  ),
});
export const getParticipatingUsersResponseSchema = z.object({ teamIds: z.array(z.number().int()) });
export const getPredictabilityResponseSchema = z.object({
  sessions: z
    .array(
      z
        .object({
          sessionId: z.string(),
          sessionName: z.string(),
          startDate: z.string().describe("ISO date derived from first iteration start"),
          endDate: z.string().describe("ISO date derived from last iteration end"),
        })
        .describe("Session metadata for predictability chart x-axis"),
    )
    .describe("Sessions ordered by start_date (x-axis labels)"),
  overall: z
    .array(
      z.union([
        z
          .object({
            totalAvRatio: z
              .union([
                z.number().describe("total_av / committed_bv, null if BV <= 0"),
                z.null().describe("total_av / committed_bv, null if BV <= 0"),
              ])
              .describe("total_av / committed_bv, null if BV <= 0")
              .optional(),
          })
          .describe("Predictability ratio for a team or overall in a single session"),
        z.null(),
      ]),
    )
    .describe("One data point per session, summed across requested teams"),
  overallMissingAv: z.array(z.boolean()).describe("One bool per session for overall"),
  teams: z
    .array(
      z
        .object({
          teamId: z.number().int(),
          values: z
            .array(
              z.union([
                z
                  .object({
                    totalAvRatio: z
                      .union([
                        z.number().describe("total_av / committed_bv, null if BV <= 0"),
                        z.null().describe("total_av / committed_bv, null if BV <= 0"),
                      ])
                      .describe("total_av / committed_bv, null if BV <= 0")
                      .optional(),
                  })
                  .describe("Predictability ratio for a team or overall in a single session"),
                z.null(),
              ]),
            )
            .describe("One per session, ordered. Null if team not in that session."),
          missingAv: z
            .array(z.boolean())
            .describe("One bool per session. True if any objective has av=None."),
        })
        .describe("Predictability data for a single team across sessions"),
    )
    .describe("One entry per requested team"),
});
export const getSessionNameResponseSchema = z.object({ name: z.string() });
export const getSessionResponseSchema = z.object({
  session: z.object({
    id: z.string(),
    name: z.string(),
    createdBy: z.union([z.string(), z.null()]).optional(),
    archived: z.boolean().optional(),
    editable: z.boolean().optional(),
    creationDate: z.number().optional(),
    planningDates: z
      .object({
        start: z.union([z.string().date(), z.null()]).optional(),
        end: z.union([z.string().date(), z.null()]).optional(),
      })
      .describe("First and last day of the PI planning event")
      .optional(),
  }),
});
export const getSessionTeamIdsMappingResponseSchema = z.object({
  mapping: z.array(z.object({ sessionId: z.string(), teamIds: z.array(z.number().int()) })),
});
export const getSessionsResponseSchema = z.object({
  sessions: z.array(
    z
      .object({
        id: z.string(),
        name: z.string(),
        createdBy: z.union([z.string(), z.null()]).optional(),
        archived: z.boolean(),
        editable: z.boolean(),
        creationDate: z.number(),
        planningDates: z
          .object({
            start: z.union([z.string().date(), z.null()]).optional(),
            end: z.union([z.string().date(), z.null()]).optional(),
          })
          .describe("First and last day of the PI planning event")
          .optional(),
        iterations: z
          .union([
            z
              .array(
                z.object({
                  id: z.number().int(),
                  start: z.number().int(),
                  end: z.number().int(),
                  name: z.string(),
                  teams: z
                    .array(
                      z.object({
                        teamId: z.number().int(),
                        sources: z.array(
                          z.object({
                            id: z.number().int().optional(),
                            sprint: z.union([z.number().int(), z.null()]).optional(),
                          }),
                        ),
                      }),
                    )
                    .optional(),
                }),
              )
              .describe("The session's iterations as the session picker shows them"),
            z.null().describe("The session's iterations as the session picker shows them"),
          ])
          .describe("The session's iterations as the session picker shows them")
          .optional(),
      })
      .describe(
        "A session as sessions.get lists it; the summary fields are guaranteed, unlike on the single-session read",
      ),
  ),
});
export const getSettingsResponseSchema = z.object({
  settings: z
    .object({
      iterationLoadCritical: z.number().int().optional(),
      confirmStickyDelete: z.boolean().optional(),
      teamForbidStickyDelete: z.boolean().optional(),
      moveActivated: z.boolean().optional(),
      iterationLoadWarn: z.union([z.number().int(), z.null()]).optional(),
      backlogForbidJIRAStickyDelete: z.boolean().optional(),
      backlogForbidWSJFEdit: z.boolean().optional(),
      backlogSynchronizeADOIteration: z.boolean().optional(),
      backlogMirrorAssignedStickiesToTeamBoard: z.boolean().optional(),
      backlogSynchronizeRallyTeams: z.boolean().optional(),
      teamMoveUnmappedToBacklog: z.boolean().optional(),
      showDevLinks: z.boolean().optional(),
      autoDependencyBridge: z.boolean().optional(),
      backlogMirrorAssignedStickiesToProgramBoardAlm: z.boolean().optional(),
      backlogSynchronizeADOTeam: z.boolean().optional(),
    })
    .optional(),
});
export const getStatusDistributionResponseSchema = z.object({
  items: z.array(
    z.object({
      statusClass: z.string(),
      statusName: z.string(),
      totalStoryPoints: z.number(),
      itemsCount: z.number().int(),
      stickyIds: z.array(z.string()),
    }),
  ),
});
export const getStickerTypesResponseSchema = z.object({
  stickerTypes: z.array(
    z
      .object({
        icon: z.enum(["flag", "check_mark", "dot", "star", "question_mark", "exclamation_mark"]),
        color: z.enum(["red", "orange", "yellow", "blue", "purple", "green"]),
        label: z.string(),
      })
      .describe("Sticker type embedded on the session document. Order is preserved by the array."),
  ),
});
export const getStickiesByBoardIdsResponseSchema = z.object({
  stickies: z.array(
    z.object({
      id: z.string(),
      almIssueId: z.union([z.number().int(), z.string()]).optional(),
      boardId: z.string(),
      WSJFValue: z.union([z.number(), z.null()]).optional(),
      almIssueUrl: z.string().optional(),
      stackPos: z.number().int().optional(),
      text: z.string().optional(),
      typeId: z.string(),
      statusClass: z.enum(["todo", "in-progress", "done", "undefined"]).optional(),
      status: z.string().optional(),
      aCenter: z.tuple([z.number(), z.number()]).optional(),
      teamId: z.union([z.number().int(), z.null()]).optional(),
      iterationNumber: z.union([z.number().int(), z.null()]).optional(),
      almSource: z.union([z.number().int(), z.string(), z.null()]).optional(),
      storyPoints: z.union([z.number(), z.null()]).optional(),
      risk: z.enum(["R", "O", "A", "M", ""]).optional(),
      dependTeamId: z.union([z.number().int(), z.null()]).optional(),
      dependTeam: z.union([z.string(), z.null()]).optional(),
      objectives: z.array(z.object({ id: z.string(), boardId: z.string() })).optional(),
      precondTeamId: z.number().int().optional(),
      precondTeam: z.string().optional(),
      flagType: z.union([z.string(), z.null()]).optional(),
      stickyGroup: z.union([z.string(), z.null()]).optional(),
      artId: z.union([z.number().int(), z.null()]).optional(),
      transition: z.string().optional(),
      reactions: z.record(z.string(), z.array(z.string())).optional(),
      assignee: z
        .union([
          z.object({
            pipId: z
              .union([
                z.string().describe("PI planning user id, null when the user is not mapped"),
                z.null().describe("PI planning user id, null when the user is not mapped"),
              ])
              .describe("PI planning user id, null when the user is not mapped"),
            almId: z
              .union([
                z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
              ])
              .describe("ALM user id, null when the user is not a mapped ALM user"),
          }),
          z.null(),
        ])
        .optional(),
      reporter: z
        .union([
          z.object({
            pipId: z
              .union([
                z.string().describe("PI planning user id, null when the user is not mapped"),
                z.null().describe("PI planning user id, null when the user is not mapped"),
              ])
              .describe("PI planning user id, null when the user is not mapped"),
            almId: z
              .union([
                z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
              ])
              .describe("ALM user id, null when the user is not a mapped ALM user"),
          }),
          z.null(),
        ])
        .optional(),
      stepValue: z
        .union([
          z
            .number()
            .int()
            .describe(
              "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
            ),
          z
            .null()
            .describe(
              "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
            ),
        ])
        .describe(
          "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
        )
        .optional(),
      templateId: z.string().optional(),
      isOrigin: z.boolean().optional(),
      linkedStickies: z
        .array(
          z.object({ linkId: z.string(), stickyId: z.string(), direction: z.enum(["from", "to"]) }),
        )
        .optional(),
      statusDistribution: z
        .union([
          z
            .record(z.string(), z.object({ points: z.number(), value: z.number().int() }))
            .describe("Progress of the linked child stickies by status class"),
          z.null(),
        ])
        .optional(),
    }),
  ),
});
export const getStickiesResponseSchema = z.object({
  stickies: z.array(
    z.object({
      id: z.string(),
      almIssueId: z.union([z.number().int(), z.string()]).optional(),
      boardId: z.string(),
      WSJFValue: z.union([z.number(), z.null()]).optional(),
      almIssueUrl: z.string().optional(),
      stackPos: z.number().int().optional(),
      text: z.string().optional(),
      typeId: z.string(),
      statusClass: z.enum(["todo", "in-progress", "done", "undefined"]).optional(),
      status: z.string().optional(),
      aCenter: z.tuple([z.number(), z.number()]).optional(),
      teamId: z.union([z.number().int(), z.null()]).optional(),
      iterationNumber: z.union([z.number().int(), z.null()]).optional(),
      almSource: z.union([z.number().int(), z.string(), z.null()]).optional(),
      storyPoints: z.union([z.number(), z.null()]).optional(),
      risk: z.enum(["R", "O", "A", "M", ""]).optional(),
      dependTeamId: z.union([z.number().int(), z.null()]).optional(),
      dependTeam: z.union([z.string(), z.null()]).optional(),
      objectives: z.array(z.object({ id: z.string(), boardId: z.string() })).optional(),
      precondTeamId: z.number().int().optional(),
      precondTeam: z.string().optional(),
      flagType: z.union([z.string(), z.null()]).optional(),
      stickyGroup: z.union([z.string(), z.null()]).optional(),
      artId: z.union([z.number().int(), z.null()]).optional(),
      transition: z.string().optional(),
      reactions: z.record(z.string(), z.array(z.string())).optional(),
      assignee: z
        .union([
          z.object({
            pipId: z
              .union([
                z.string().describe("PI planning user id, null when the user is not mapped"),
                z.null().describe("PI planning user id, null when the user is not mapped"),
              ])
              .describe("PI planning user id, null when the user is not mapped"),
            almId: z
              .union([
                z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
              ])
              .describe("ALM user id, null when the user is not a mapped ALM user"),
          }),
          z.null(),
        ])
        .optional(),
      reporter: z
        .union([
          z.object({
            pipId: z
              .union([
                z.string().describe("PI planning user id, null when the user is not mapped"),
                z.null().describe("PI planning user id, null when the user is not mapped"),
              ])
              .describe("PI planning user id, null when the user is not mapped"),
            almId: z
              .union([
                z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
              ])
              .describe("ALM user id, null when the user is not a mapped ALM user"),
          }),
          z.null(),
        ])
        .optional(),
      stepValue: z
        .union([
          z
            .number()
            .int()
            .describe(
              "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
            ),
          z
            .null()
            .describe(
              "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
            ),
        ])
        .describe(
          "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
        )
        .optional(),
      templateId: z.string().optional(),
      isOrigin: z.boolean().optional(),
      linkedStickies: z
        .array(
          z.object({ linkId: z.string(), stickyId: z.string(), direction: z.enum(["from", "to"]) }),
        )
        .optional(),
      statusDistribution: z
        .union([
          z
            .record(z.string(), z.object({ points: z.number(), value: z.number().int() }))
            .describe("Progress of the linked child stickies by status class"),
          z.null(),
        ])
        .optional(),
    }),
  ),
});
export const getStickyChangesResponseSchema = z.object({
  changes: z.array(
    z.union([
      z.object({
        kind: z.literal("create"),
        stickyId: z.string(),
        timestamp: z.number().int(),
        userId: z.string(),
      }),
      z.object({
        kind: z.literal("update"),
        stickyId: z.string(),
        timestamp: z.number().int(),
        userId: z.string(),
        changes: z.array(
          z
            .object({ key: z.string(), old: z.any().optional(), new: z.any().optional() })
            .describe(
              "One changed sticky field; old/new carry the field's raw value (any JSON type)",
            ),
        ),
      }),
      z.object({
        kind: z.literal("delete"),
        stickyId: z.string(),
        timestamp: z.number().int(),
        userId: z.string(),
        changes: z.array(
          z
            .object({ key: z.string(), old: z.any().optional(), new: z.any().optional() })
            .describe(
              "One changed sticky field; old/new carry the field's raw value (any JSON type)",
            ),
        ),
      }),
      z.object({
        kind: z.literal("mirror"),
        stickyId: z.string(),
        timestamp: z.number().int(),
        userId: z.string(),
        fromBoard: z.string(),
        toBoard: z.string(),
      }),
      z.object({
        kind: z.literal("unmirror"),
        stickyId: z.string(),
        timestamp: z.number().int(),
        userId: z.string(),
        fromBoard: z.string(),
        toBoard: z.string(),
      }),
      z.object({
        kind: z.literal("link"),
        stickyId: z.string(),
        timestamp: z.number().int(),
        userId: z.string(),
        linkedTo: z.string(),
      }),
      z.object({
        kind: z.literal("unlink"),
        stickyId: z.string(),
        timestamp: z.number().int(),
        userId: z.string(),
        linkedTo: z.string(),
      }),
    ]),
  ),
});
export const getStickyResponseSchema = z.object({
  sticky: z
    .object({
      id: z.string(),
      almIssueId: z.union([z.number().int(), z.string()]).optional(),
      boardId: z.string(),
      WSJFValue: z.union([z.number(), z.null()]).optional(),
      almIssueUrl: z.string().optional(),
      stackPos: z.number().int().optional(),
      text: z.string().optional(),
      typeId: z.string(),
      statusClass: z.enum(["todo", "in-progress", "done", "undefined"]).optional(),
      status: z.string().optional(),
      aCenter: z.tuple([z.number(), z.number()]).optional(),
      teamId: z.union([z.number().int(), z.null()]).optional(),
      iterationNumber: z.union([z.number().int(), z.null()]).optional(),
      almSource: z.union([z.number().int(), z.string(), z.null()]).optional(),
      storyPoints: z.union([z.number(), z.null()]).optional(),
      risk: z.enum(["R", "O", "A", "M", ""]).optional(),
      dependTeamId: z.union([z.number().int(), z.null()]).optional(),
      dependTeam: z.union([z.string(), z.null()]).optional(),
      objectives: z.array(z.object({ id: z.string(), boardId: z.string() })).optional(),
      precondTeamId: z.number().int().optional(),
      precondTeam: z.string().optional(),
      flagType: z.union([z.string(), z.null()]).optional(),
      stickyGroup: z.union([z.string(), z.null()]).optional(),
      artId: z.union([z.number().int(), z.null()]).optional(),
      transition: z.string().optional(),
      reactions: z.record(z.string(), z.array(z.string())).optional(),
      assignee: z
        .union([
          z.object({
            pipId: z
              .union([
                z.string().describe("PI planning user id, null when the user is not mapped"),
                z.null().describe("PI planning user id, null when the user is not mapped"),
              ])
              .describe("PI planning user id, null when the user is not mapped"),
            almId: z
              .union([
                z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
              ])
              .describe("ALM user id, null when the user is not a mapped ALM user"),
          }),
          z.null(),
        ])
        .optional(),
      reporter: z
        .union([
          z.object({
            pipId: z
              .union([
                z.string().describe("PI planning user id, null when the user is not mapped"),
                z.null().describe("PI planning user id, null when the user is not mapped"),
              ])
              .describe("PI planning user id, null when the user is not mapped"),
            almId: z
              .union([
                z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
              ])
              .describe("ALM user id, null when the user is not a mapped ALM user"),
          }),
          z.null(),
        ])
        .optional(),
      stepValue: z
        .union([
          z
            .number()
            .int()
            .describe(
              "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
            ),
          z
            .null()
            .describe(
              "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
            ),
        ])
        .describe(
          "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
        )
        .optional(),
      templateId: z.string().optional(),
      isOrigin: z.boolean().optional(),
      linkedStickies: z
        .array(
          z.object({ linkId: z.string(), stickyId: z.string(), direction: z.enum(["from", "to"]) }),
        )
        .optional(),
      statusDistribution: z
        .union([
          z
            .record(z.string(), z.object({ points: z.number(), value: z.number().int() }))
            .describe("Progress of the linked child stickies by status class"),
          z.null(),
        ])
        .optional(),
    })
    .optional(),
});
export const getTeamsResponseSchema = z.object({
  teams: z.array(
    z.object({
      id: z.number().int(),
      name: z.string(),
      boardId: z.union([z.string(), z.null()]).optional(),
      archived: z.boolean(),
      external: z.boolean(),
      artId: z.number().int().optional(),
      userId: z.number().int().optional(),
    }),
  ),
});
export const getTimerEventsResponseSchema = z.object({
  timerEvents: z.array(
    z.object({
      id: z.string(),
      boardId: z.union([z.string(), z.null()]).optional(),
      artId: z.union([z.string(), z.null()]).optional(),
      createdBy: z.string(),
      createdAt: z.number().int(),
      updatedBy: z.string(),
      updatedAt: z.number().int(),
      data: z.object({
        name: z.string(),
        state: z.enum(["running", "paused", "stopped"]),
        duration: z.number(),
        end: z.number().describe("Timer end as epoch milliseconds; may be fractional"),
      }),
    }),
  ),
});
export const hasStickiesOfTypeOnBoardsResponseSchema = z.object({ hasStickies: z.boolean() });
export const hasStickiesOfTypeResponseSchema = z.object({ hasStickies: z.boolean() });
export const importResultResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});
export const linkCreatedEventSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  fromStickyId: z.string(),
  toStickyId: z.string(),
  linkTypeId: z.string().optional(),
});
export const linkDeletedEventSchema = z.string();
export const noDataSchema = z.record(z.string(), z.never());
export const searchStickiesResponseSchema = z.object({
  stickies: z.array(
    z.object({
      id: z.string(),
      almIssueId: z.union([z.number().int(), z.string()]).optional(),
      boardId: z.string(),
      WSJFValue: z.union([z.number(), z.null()]).optional(),
      almIssueUrl: z.string().optional(),
      stackPos: z.number().int().optional(),
      text: z.string().optional(),
      typeId: z.string(),
      statusClass: z.enum(["todo", "in-progress", "done", "undefined"]).optional(),
      status: z.string().optional(),
      aCenter: z.tuple([z.number(), z.number()]).optional(),
      teamId: z.union([z.number().int(), z.null()]).optional(),
      iterationNumber: z.union([z.number().int(), z.null()]).optional(),
      almSource: z.union([z.number().int(), z.string(), z.null()]).optional(),
      storyPoints: z.union([z.number(), z.null()]).optional(),
      risk: z.enum(["R", "O", "A", "M", ""]).optional(),
      dependTeamId: z.union([z.number().int(), z.null()]).optional(),
      dependTeam: z.union([z.string(), z.null()]).optional(),
      objectives: z.array(z.object({ id: z.string(), boardId: z.string() })).optional(),
      precondTeamId: z.number().int().optional(),
      precondTeam: z.string().optional(),
      flagType: z.union([z.string(), z.null()]).optional(),
      stickyGroup: z.union([z.string(), z.null()]).optional(),
      artId: z.union([z.number().int(), z.null()]).optional(),
      transition: z.string().optional(),
      reactions: z.record(z.string(), z.array(z.string())).optional(),
      assignee: z
        .union([
          z.object({
            pipId: z
              .union([
                z.string().describe("PI planning user id, null when the user is not mapped"),
                z.null().describe("PI planning user id, null when the user is not mapped"),
              ])
              .describe("PI planning user id, null when the user is not mapped"),
            almId: z
              .union([
                z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
              ])
              .describe("ALM user id, null when the user is not a mapped ALM user"),
          }),
          z.null(),
        ])
        .optional(),
      reporter: z
        .union([
          z.object({
            pipId: z
              .union([
                z.string().describe("PI planning user id, null when the user is not mapped"),
                z.null().describe("PI planning user id, null when the user is not mapped"),
              ])
              .describe("PI planning user id, null when the user is not mapped"),
            almId: z
              .union([
                z.string().describe("ALM user id, null when the user is not a mapped ALM user"),
                z.null().describe("ALM user id, null when the user is not a mapped ALM user"),
              ])
              .describe("ALM user id, null when the user is not a mapped ALM user"),
          }),
          z.null(),
        ])
        .optional(),
      stepValue: z
        .union([
          z
            .number()
            .int()
            .describe(
              "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
            ),
          z
            .null()
            .describe(
              "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
            ),
        ])
        .describe(
          "Id of the sticky's step estimate, one of the type's step_estimate_values; null when it has none",
        )
        .optional(),
      templateId: z.string().optional(),
      isOrigin: z.boolean().optional(),
      linkedStickies: z
        .array(
          z.object({ linkId: z.string(), stickyId: z.string(), direction: z.enum(["from", "to"]) }),
        )
        .optional(),
      statusDistribution: z
        .union([
          z
            .record(z.string(), z.object({ points: z.number(), value: z.number().int() }))
            .describe("Progress of the linked child stickies by status class"),
          z.null(),
        ])
        .optional(),
    }),
  ),
});
export const sessionPlanningDatesSchema = z
  .object({
    start: z.union([z.string().date(), z.null()]).optional(),
    end: z.union([z.string().date(), z.null()]).optional(),
  })
  .describe("First and last day of the PI planning event");
export const setStickerTypesResponseSchema = z.object({
  stickerTypes: z.array(
    z
      .object({
        icon: z.enum(["flag", "check_mark", "dot", "star", "question_mark", "exclamation_mark"]),
        color: z.enum(["red", "orange", "yellow", "blue", "purple", "green"]),
        label: z.string(),
      })
      .describe("Sticker type embedded on the session document. Order is preserved by the array."),
  ),
});
export const settingUpdatedEventSchema = z.object({
  iterationLoadCritical: z.number().int().optional(),
  confirmStickyDelete: z.boolean().optional(),
  teamForbidStickyDelete: z.boolean().optional(),
  moveActivated: z.boolean().optional(),
  iterationLoadWarn: z.union([z.number().int(), z.null()]).optional(),
  backlogForbidJIRAStickyDelete: z.boolean().optional(),
  backlogForbidWSJFEdit: z.boolean().optional(),
  backlogSynchronizeADOIteration: z.boolean().optional(),
  backlogMirrorAssignedStickiesToTeamBoard: z.boolean().optional(),
  backlogSynchronizeRallyTeams: z.boolean().optional(),
  teamMoveUnmappedToBacklog: z.boolean().optional(),
  showDevLinks: z.boolean().optional(),
  autoDependencyBridge: z.boolean().optional(),
  backlogMirrorAssignedStickiesToProgramBoardAlm: z.boolean().optional(),
  backlogSynchronizeADOTeam: z.boolean().optional(),
});
export const timersUpdatedEventSchema = z.object({
  timerEvents: z.array(
    z.object({
      id: z.string(),
      boardId: z.union([z.string(), z.null()]).optional(),
      artId: z.union([z.string(), z.null()]).optional(),
      createdBy: z.string(),
      createdAt: z.number().int(),
      updatedBy: z.string(),
      updatedAt: z.number().int(),
      data: z.object({
        name: z.string(),
        state: z.enum(["running", "paused", "stopped"]),
        duration: z.number(),
        end: z.number().describe("Timer end as epoch milliseconds; may be fractional"),
      }),
    }),
  ),
});
